import React, { ComponentType } from "react";
import {
    Routes,
    Route,
    NavLink,
    useLocation,
    useNavigate,
    Link,
} from "react-router-dom";
import { isBrowser } from "react-device-detect";
import { format } from "date-fns";
import {
    Social,
    Addons,
    Users,
    Profile,
    Plan,
    // Publish
} from "./sections";
import { cx } from "utils/join-classnames";
import { IconNames, Project } from "core/models";
import { Button, Icon, Logo, PasswordField } from "ui/components";
import { paths } from "ui/routes";
import usePermissions from "ui/hooks/usePermissions";
import { useSessionContext } from "ui/context/SessionContext";
import { useLivestreamContext } from "ui/context/LivestreamContext";
import {
    listProjects,
    logoutUser,
    updateLoggedIn,
    UpdateUserFields,
} from "core/services";
import { useNotifications } from "ui/context/NotificationsContext";
import styles from "./Settings.module.scss";
import pwa_styles from "./Settings_Pwa.module.scss";

interface NavItem {
    title: string;
    icon: IconNames;
    path: string;
    component: React.ReactNode;
    category: "project" | "account";
    disabled: boolean;
}

const Settings: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userPermissions = usePermissions();
    const sessionContext = useSessionContext();
    const livestreamContext = useLivestreamContext();
    const { addNotification } = useNotifications();

    const userCanConfigureSocials = userPermissions.canConfigureShare;
    const userIsProjectAdmin = userPermissions.isProjectAdmin;

    const [currentPassword, setCurrentPassword] = React.useState<string>();
    const [newPassword, setNewPassword] = React.useState<string>();
    const [newPasswordConfirm, setNewPasswordConfirm] =
        React.useState<string>();
    const [projects, setProjects] = React.useState<Project[]>([]);

    React.useEffect(() => {
        if (isBrowser) {
            if (location.pathname === paths.settings) {
                navigate(paths.settingsProfile);
            }
        }
    }, []);

    const logout = async () => {
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            await logoutUser(sessionContext.session.token);
            livestreamContext.removeAll();
            sessionContext.setSession(null);
            navigate("/login");
        } catch (err) {
            addNotification(
                "error",
                "Failed to log out",
                "please try again",
                5000,
            );
        }
    };

    const navitems: NavItem[] = [
        {
            title: "Profile",
            icon: "user",
            path: paths.settingsProfile,
            component: <Profile />,
            category: "account",
            disabled: false,
        },
    ];
    navitems.push({
        title: "Social media accounts",
        icon: "share",
        path: paths.settingsSocial,
        component: <Social />,
        category: "project",
        disabled: !userCanConfigureSocials,
    });
    navitems.push({
        title: "Addons",
        icon: "addons",
        path: paths.settingsAddons,
        component: <Addons />,
        category: "project",
        disabled: !userPermissions.canManageAddons,
    });
    navitems.push({
        title: "Users & Permissions",
        icon: "lock",
        path: paths.settingsUsers,
        component: <Users />,
        category: "project",
        disabled: !userIsProjectAdmin,
    });

    // Feature will be released as soon as new project permissions are implemented... //
    //
    // navitems.push({
    //     title: "Publish platform",
    //     icon: "publish",
    //     path: paths.settingsPublish,
    //     component: Publish,
    //     category: "project",
    //     disabled: !userIsProjectAdmin,
    // });

    // Feature is hidden for now
    // navitems.push({
    //     title: "Plan",
    //     icon: "check",
    //     path: paths.settingsPlan,
    //     component: Plan,
    //     category: "project",
    //     disabled: false,
    // });

    const save = async () => {
        if (newPassword !== newPasswordConfirm) {
            addNotification(
                "error",
                "Passwords didn't match",
                "Please try again",
                5000,
            );
            return;
        }
        if (!currentPassword || currentPassword === "") {
            addNotification(
                "error",
                "Fill in your current password",
                "We really do need it",
                5000,
            );
            return;
        }
        if (
            !newPassword ||
            newPassword === "" ||
            !newPasswordConfirm ||
            newPasswordConfirm === ""
        ) {
            addNotification(
                "error",
                "Please fill in all fields",
                "We need all the info we can get",
                5000,
            );
            return;
        }
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            const updateObject: UpdateUserFields = {
                currentPassword,
                password: newPassword,
            };
            const updateUser = await updateLoggedIn(
                sessionContext.session.token,
                updateObject,
            );
            if (updateUser.error) {
                throw Error(updateUser.error);
            }
            addNotification(
                "success",
                `password successfully changed`,
                "Please make sure you remember it",
                0,
            );
        } catch (error) {
            addNotification("error", `[ERROR] ${error}`, "error", 0);
        }
    };

    const init = async () => {
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            const listProjectsResult = await listProjects(
                sessionContext.session.token,
                100,
            );

            if (listProjectsResult.error || !listProjectsResult.value) {
                throw new Error("error");
            }
            setProjects(listProjectsResult.value);
        } catch (err) {
            addNotification(
                "error",
                "Oops",
                "Something went wrong getting yout projects and info",
                10000,
            );
        }
    };

    React.useEffect(() => {
        init();
    }, []);

    if (!isBrowser) {
        return (
            <div className={pwa_styles.page}>
                <div className={pwa_styles.header}>
                    <div className={pwa_styles.header__icon__container}>
                        <NavLink to="/clips">
                            <Icon
                                className={pwa_styles.header__icon}
                                name="arrow-left"
                            />
                        </NavLink>
                    </div>
                    <div className={pwa_styles.header__logo}>
                        <Logo />
                    </div>
                </div>
                <div className={pwa_styles.subheader}>
                    <h3 className={pwa_styles.subheader__title}>Profile</h3>
                    <div className={pwa_styles.subheader__hr} />
                </div>
                <div className={pwa_styles.tabledata__row}>
                    <div className={pwa_styles.tabledata__row__text}>
                        Logged in as
                    </div>
                    <div>
                        {sessionContext.session?.user.username}{" "}
                        {!sessionContext.session?.user.verified &&
                            "(not verified)"}
                    </div>
                </div>
                <div className={pwa_styles.tabledata__row}>
                    <div className={pwa_styles.tabledata__row__text}>
                        Project(s)
                    </div>
                    <div>
                        <ul className={pwa_styles.projectslist}>
                            {projects.map((project) => (
                                <li key={project.id}>{project.name}</li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className={pwa_styles.tabledata__row}>
                    <div className={pwa_styles.tabledata__row__text}>
                        Registered
                    </div>
                    <div>
                        {sessionContext.session &&
                            format(
                                new Date(
                                    sessionContext.session.user.creationDate,
                                ),
                                "dd MMMM yyyy",
                            )}
                    </div>
                </div>
                <div className={pwa_styles.tabledata__row}>
                    <div className={pwa_styles.tabledata__row__text}>
                        SpotOn Version
                    </div>
                    <div>
                        {
                            SPOTON_APPVERSION
                            // Global const defined in vite.config.ts, source: package.json
                        }
                    </div>
                </div>
                <div className={pwa_styles.subheader}>
                    <h3 className={pwa_styles.subheader__title}>
                        Change password
                    </h3>
                    <div className={pwa_styles.subheader__hr} />
                </div>
                <div className={pwa_styles.passwordfield}>
                    <div className={pwa_styles.passwordfield__title}>
                        Current password
                    </div>
                    <div>
                        <PasswordField
                            theme="light"
                            defaultText="Current password"
                            onValueChanged={(val) => setCurrentPassword(val)}
                        />
                    </div>
                </div>
                <div className={pwa_styles.passwordfield}>
                    <div className={pwa_styles.passwordfield__title}>
                        New password
                    </div>
                    <div>
                        <PasswordField
                            theme="light"
                            defaultText="New password"
                            onValueChanged={(val) => setNewPassword(val)}
                        />
                    </div>
                </div>
                <div className={pwa_styles.passwordfield}>
                    <div className={pwa_styles.passwordfield__title}>
                        Confirm new password
                    </div>
                    <div>
                        <PasswordField
                            theme="light"
                            defaultText="Confirm new password"
                            onValueChanged={(val) => setNewPasswordConfirm(val)}
                        />
                    </div>
                </div>
                <div className={pwa_styles.passwordfield__buttoncontainer}>
                    <div />
                    <div>
                        <Button
                            className={
                                pwa_styles.passwordfield__buttoncontainer__btn
                            }
                            type="primary"
                            theme="dark"
                            onClick={save}
                        >
                            Save
                        </Button>
                    </div>
                </div>
                <div className={pwa_styles.subheader__hr} />

                <div className={pwa_styles.logout}>
                    <div
                        role="presentation"
                        className={pwa_styles.logout__button}
                        onClick={logout}
                    >
                        <Icon
                            className={pwa_styles.logout__button__icon}
                            name="logout"
                        />
                        <span className={pwa_styles.logout__button__text}>
                            Logout
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.subnavbar}>
                <div className={styles.subnavbar__title}>Settings</div>
                <nav className={styles.subnavbar__nav}>
                    <div className={styles.subnavbar__category}>Account</div>
                    {navitems
                        .filter((nav) => nav.category === "account")
                        .map((navitem) => (
                            <NavLink
                                to={navitem.path}
                                className={({ isActive }) =>
                                    cx(
                                        styles.subnavbar__nav__item,
                                        isActive &&
                                            styles.subnavbar__nav__item__active,
                                        navitem.disabled &&
                                            styles[
                                                "subnavbar__nav__item--disabled"
                                            ],
                                    )
                                }
                                key={navitem.path}
                            >
                                <Icon
                                    name={navitem.icon}
                                    className={styles.subnavbar__nav__icon}
                                />
                                {navitem.title}
                            </NavLink>
                        ))}
                    {navitems.filter((nav) => nav.category === "project")
                        .length !== 0 && (
                        <div className={styles.subnavbar__category}>
                            Project
                        </div>
                    )}

                    {navitems
                        .filter((nav) => nav.category === "project")
                        .map((navitem) => (
                            <NavLink
                                to={navitem.path}
                                className={({ isActive }) =>
                                    cx(
                                        styles.subnavbar__nav__item,
                                        isActive &&
                                            styles.subnavbar__nav__item__active,
                                        navitem.disabled &&
                                            styles[
                                                "subnavbar__nav__item--disabled"
                                            ],
                                    )
                                }
                                key={navitem.path}
                            >
                                <Icon
                                    name={navitem.icon}
                                    className={styles.subnavbar__nav__icon}
                                />
                                {navitem.title}
                            </NavLink>
                        ))}
                </nav>
            </div>
            <div className={styles.settings}>
                <Routes>
                    {navitems.map((navitem) => (
                        <Route
                            key={navitem.path}
                            path={navitem.path}
                            element={navitem.component}
                        />
                    ))}
                    <Route path="/settings/*" />
                </Routes>
            </div>
        </div>
    );
};

export default Settings;
